import { UseQueryResult } from '@tanstack/react-query';
import { ExportCategory } from './export';
import { TOutreachType } from './outreachType';

// Base filter params shared across most report endpoints
export type ReportFilterParams = {
  project_id?: string;
  user_id?: string;
  start_datetime?: string;
  end_datetime?: string;
  voter_id?: string;
  county_name?: string;
  precinct_id?: string;
  congressional_district?: string;
  state_leg_upper_district?: string;
  state_leg_lower_district?: string;
  dma_name?: string;
  team_id?: string;
};

export type PaginationParams = {
  limit?: number;
  offset?: number;
};

// Composed payload types
export type ActivityFeedPayload = ReportFilterParams &
  PaginationParams & {
    outreach_type?: TOutreachType | null;
    voter_id?: string;
  };

export type LeaderboardPayload = ReportFilterParams & {
  filter_type?: string;
  include_archived?: boolean;
  should_group_canvasses_by_household?: boolean;
};

export type ActivityTimelinePayload = ReportFilterParams & {
  outreach_type?: string;
  include_archived?: boolean;
  should_group_canvasses_by_household?: boolean;
  timezone?: string;
};

export type ActivityCountsPayload = ReportFilterParams & {
  outreach_type?: TOutreachType | null;
  should_group_canvasses_by_household?: boolean;
};

// Activity Feed Export
export type ActivityFeedExport = {
  id: string;
  org_id: string;
  status: 'processing' | 'completed' | 'failed';
  category: ExportCategory;
  download_url: string;
  count: number | null;
  filter_params: Record<string, string | undefined>;
  created_by: string;
  created_at: string;
  updated_at: string;
};

export type ActivityFeedExportParams = Omit<ReportFilterParams, 'voter_id'> & {
  outreach_type?: TOutreachType | null;
  team_projects_id?: string;
  voter_id?: string;
  voter_tag_ids?: string[];
};

export enum ActivityType {
  CALL = 'CALL',
  TEXT = 'TEXT',
  SURVEY = 'SURVEY',
  TEXT_SURVEY = 'TEXT-SURVEY',
  IVR = 'IVR',
  CANVASS = 'CANVASS',
  TAG = 'TAG',
  NOTES = 'NOTES',
  RELATIONAL = 'RELATIONAL',
}

// Row types
export type ActivityFeedRow = {
  created_at?: string;
  updated_at?: string;
  user_id: string;
  voter_id?: string;
  project_id?: string;
  voter_first_name?: string;
  voter_last_name?: string;
  tag_name?: string;
  type?: ActivityType;
  user_first_name?: string;
  user_last_name?: string;
  tag_value?: string;
  survey_type?: string;
  question_text?: string;
  survey_name?: string;
  is_script?: boolean;
  notes?: string;
  disposition?: string;
  latitude?: string;
  longitude?: string;
  user_latitude?: string;
  user_longitude?: string;
  delta_feet?: number;
  device_geolocation_enabled?: boolean;
  device_geolocation_permission_status?: string;
  is_using_emulator?: boolean;
  device_id?: string;
  is_paid_interaction?: boolean;
  paid_interaction_verification_submitted_at?: Date;
  paid_interaction_payment_at?: Date;
  paid_interaction_payment_amount?: number;
  is_inbound?: boolean;
};

export type LeaderboardRow = {
  org_id?: string;
  org_name?: string;
  id: string;
  name?: string;
  email?: string;
  phone_number?: string;
  tags: number;
  calls: number;
  calls_answered: number;
  texts: number;
  doors: number;
  doors_flagged: number;
  doors_canvassed: number;
  doors_canvassed_flagged: number;
  doors_no_gps: number;
  doors_canvassed_no_gps: number;
  text_surveys: number;
  ivrs: number;
  surveys: number;
  relational: number;
  uploadcare_uuid?: string;
  picture_url?: string;
  signed_url?: string;
};

export type ActivityTimeline = {
  name: string;
  data: [number, number][];
}[];

export type ActivityCountEntry = {
  name: string | null;
  count: number;
  percentage: number;
};

export type TagActivityCountEntry = {
  name: string;
  values: ActivityCountEntry[];
};

export type ActivityCountResults = {
  doors: ActivityCountEntry[];
  calls: ActivityCountEntry[];
  tags: TagActivityCountEntry[];
};

// Hook types
export type UseActivityTimelineType = (
  params: ActivityTimelinePayload,
  config?: object
) => UseQueryResult<ActivityTimeline, unknown>;

export type UseLeaderboardType = (
  params: LeaderboardPayload,
  config?: object
) => UseQueryResult<LeaderboardRow[], unknown>;
